import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog } from '@components/dialog/Dialog';
import { Form } from '@components/forms';
import { FormSection } from '@components/forms/FormSection';
import { FormFieldType, TFormField } from '@models/forms/formFields.type';
import { PolicyGetResponse } from '@lio/api-types';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { useCreateReviewAtRenewal, useDeleteReviewAtRenewal } from '@services/policy';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { Typography, Box } from '@mui/material';
import { ReviewReason } from '@lio/rule-engine';

import { REVIEW_REASON_OPTIONS, REVIEW_REASON_LABELS } from '@constants/reviewReason';

export interface ReviewAtRenewalModalsProps {
  open: boolean;
  modalType: 'mark' | 'remove';
  policy: PolicyGetResponse;
  onClose: () => void;
}

type ReviewAtRenewalFormData = {
  reviewReason: string;
};

export const ReviewAtRenewalModals = ({ open, modalType, policy, onClose }: ReviewAtRenewalModalsProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createReviewAtRenewal } = useCreateReviewAtRenewal();
  const { deleteReviewAtRenewal } = useDeleteReviewAtRenewal();

  const methods = useForm<ReviewAtRenewalFormData>({
    mode: 'onChange',
    defaultValues: {
      reviewReason: '',
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const policyNumber = policy
    ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
    : '';

  const handleMarkForReview = async (data: ReviewAtRenewalFormData) => {
    setIsSubmitting(true);
    try {
      await createReviewAtRenewal({
        policyId: policy.id,
        body: {
          reviewReason: data.reviewReason as any,
        },
      });
      enqueueSnackbar('Policy marked for Review at Renewal successfully', { variant: 'success' });
      onClose();
    } catch (error) {
      const message = await getApiErrorMessage(error, 'Failed to mark policy for Review at Renewal');
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveReview = async () => {
    setIsSubmitting(true);
    try {
      await deleteReviewAtRenewal({ policyId: policy.id });
      enqueueSnackbar('Review at Renewal removed successfully', { variant: 'success' });
      onClose();
    } catch (error) {
      const message = await getApiErrorMessage(error, 'Failed to remove Review at Renewal');
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (modalType === 'mark') {
    const formFields: { [fieldName: string]: TFormField } = {
      reviewReason: {
        gridWidth: { xs: 12 },
        name: 'reviewReason',
        label: 'Reason for Review',
        fieldType: FormFieldType.SelectField,
        required: true,
        fieldConfig: {
          selectField: {
            options: REVIEW_REASON_OPTIONS,
          },
        },
      },
    };

    return (
      <Dialog
        open={open}
        title={`Mark ${policyNumber} for Review at Renewal`}
        primaryBtnText='Mark for Review at Renewal'
        secondaryBtnText='Exit'
        primaryBtnAction={handleSubmit(handleMarkForReview)}
        secondaryBtnAction={onClose}
        handleCloseDialog={onClose}
        isPrimaryBtnDisabled={!isValid || isSubmitting}
        isPrimaryBtnLoading={isSubmitting}
      >
        <Box sx={{ padding: '16px 24px' }}>
          <Typography variant='body2' color='text.secondary' fontWeight='normal' sx={{ mb: 2 }}>
            Marking a Policy for Review at Renewal will force the next renewal to be manually reviewed by an Underwriter
            before it is released to a Broker.
          </Typography>

          <Form methods={methods} formId='reviewAtRenewalForm'>
            <FormSection fields={formFields} />
          </Form>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      title='Remove Review at Renewal'
      primaryBtnText='Remove Review at Renewal'
      secondaryBtnText='Exit'
      primaryBtnAction={handleRemoveReview}
      secondaryBtnAction={onClose}
      handleCloseDialog={onClose}
      isPrimaryBtnDisabled={isSubmitting}
      isPrimaryBtnLoading={isSubmitting}
    >
      <Box sx={{ padding: '16px 24px' }}>
        <Typography variant='body2' color='text.secondary' fontWeight='normal' sx={{ mb: 2 }}>
          {policyNumber} is currently marked for Review at Renewal for{' '}
          <Box component='span' sx={{ fontWeight: 'bold' }}>
            {REVIEW_REASON_LABELS[policy?.reviewsAtRenewal[0].reviewReason as ReviewReason] || 'Unknown Reason'}
          </Box>
          . Are you sure you want to remove this flag?
        </Typography>
      </Box>
    </Dialog>
  );
};
